/*
 * sg-newsletter-popup — 3-step signup popup → Klaviyo client-side subscription (double opt-in).
 *
 * Opens once after the trigger element has been viewed and then scrolled out of view (either
 * direction). Step 1 multiple choice → step 2 email → step 3 confirmation. On submit, POSTs the
 * email + chosen option (as a profile property) to Klaviyo's /client/subscriptions; the list's
 * opt-in setting drives the DOI confirmation email. Reads the data-sg-popup-* hooks from
 * sections/sg-newsletter-popup.liquid. Safe to restyle; do not rename the hooks.
 */
(function () {
  'use strict';

  // The section emits one <script> tag per instance; with two popups on a page that is two
  // identical tags. Run the module once — boot() already sweeps every [data-sg-popup] root.
  if (window.__sgPopupInit) return;
  window.__sgPopupInit = true;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var KLAVIYO_EVENTS_ENDPOINT = 'https://a.klaviyo.com/client/events';
  var KLAVIYO_REVISION = '2026-04-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var STORE_KEY = 'sg_nl_popup';
  var DAY_MS = 24 * 60 * 60 * 1000;

  var registry = Object.create(null); // popup handle -> its open() fn
  var firstOpen = null; // fallback opener when an opener names no/unknown handle

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeState(patch) {
    var next = readState();
    for (var k in patch) next[k] = patch[k];
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch (e) {
      /* storage unavailable — popup just won't remember */
    }
  }

  function suppressed(frequencyDays) {
    var s = readState();
    if (s.signedUp) return true;
    if (s.dismissedAt && Date.now() - s.dismissedAt < frequencyDays * DAY_MS) return true;
    return false;
  }

  function init(root) {
    if (root.dataset.sgPopupReady) return;
    root.dataset.sgPopupReady = 'true';

    // Portal to <body> so no transformed/overflow ancestor traps the fixed overlay
    // (otherwise backdrop-filter can't sample the page to blur it).
    if (root.parentNode !== document.body) document.body.appendChild(root);

    var triggerSelector = root.getAttribute('data-sg-popup-trigger') || '#sg-offer';

    var frequencyDays = parseInt(root.getAttribute('data-sg-popup-frequency'), 10);
    if (isNaN(frequencyDays)) frequencyDays = 7;

    var companyId = root.getAttribute('data-sg-klaviyo-company');
    var listId = root.getAttribute('data-sg-klaviyo-list');
    var propertyName = root.getAttribute('data-sg-popup-property') || 'signup_goal';
    // Signup source tag, project convention "placement:offer" (e.g. "qr-product:newsletter-10").
    // Split into two exact-match Klaviyo dimensions; the full slug stays as custom_source.
    var source = root.getAttribute('data-sg-popup-source') || '';
    var sourceParts = source.split(':');
    var placement = sourceParts[0] || '';
    var offer = sourceParts.length > 1 ? sourceParts.slice(1).join(':') : '';

    var dialog = root.querySelector('[data-sg-popup-dialog]');
    var backdrop = root.querySelector('[data-sg-popup-backdrop]');
    var closeBtns = root.querySelectorAll('[data-sg-popup-close]');
    var screens = root.querySelectorAll('[data-sg-popup-screen]');
    var optionBtns = root.querySelectorAll('[data-sg-popup-option]');
    var form = root.querySelector('[data-sg-popup-form]');
    var emailInput = root.querySelector('[data-sg-popup-email]');
    var submitBtn = root.querySelector('[data-sg-popup-submit]');
    var status = root.querySelector('[data-sg-popup-status]');
    var errorEl = root.querySelector('[data-sg-popup-error]');
    var errorText = (errorEl && errorEl.textContent.trim()) || 'Something went wrong.';

    var opened = false;
    var hasViewed = false;
    var chosenKey = null;
    var lastFocused = null;

    /* ---- screen switching ---- */
    function showScreen(n) {
      for (var i = 0; i < screens.length; i++) {
        var s = screens[i];
        var match = s.getAttribute('data-sg-popup-screen') === String(n);
        s.hidden = !match;
        if (match) {
          var heading = s.querySelector('.sg-popup__heading');
          if (heading) {
            if (heading.id) dialog.setAttribute('aria-labelledby', heading.id);
            heading.focus();
          }
        }
      }
    }

    /* ---- focus trap ---- */
    function focusables() {
      return dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
    }
    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---- open / close ---- */
    function open() {
      if (opened) return;
      opened = true;
      lastFocused = document.activeElement;
      root.hidden = false;
      document.addEventListener('keydown', onKeydown);
      showScreen(1);
    }

    function close() {
      root.hidden = true;
      opened = false; // allow an explicit opener (e.g. a button) to reopen it
      document.removeEventListener('keydown', onKeydown);
      if (!readState().signedUp) writeState({ dismissedAt: Date.now() });
      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    }

    for (var c = 0; c < closeBtns.length; c++) closeBtns[c].addEventListener('click', close);
    // dim is on the root now — clicking outside the dialog (i.e. on the root itself) closes
    root.addEventListener('click', function (e) {
      if (e.target === root) close();
    });

    /* ---- step 1: choose an option ---- */
    for (var j = 0; j < optionBtns.length; j++) {
      optionBtns[j].addEventListener('click', function () {
        chosenKey = this.getAttribute('data-sg-popup-option-key');
        showScreen(2);
        if (emailInput) emailInput.focus();
      });
    }

    /* ---- step 2: submit email ---- */
    function showStatus(message) {
      if (!status) return;
      status.textContent = message;
      status.hidden = false;
    }
    function setBusy(busy) {
      submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      submitBtn.disabled = busy;
      emailInput.disabled = busy;
    }

    function subscribe(email) {
      var attrs = {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      };
      var props = {};
      if (chosenKey) props[propertyName] = chosenKey;
      if (placement) props.signup_placement = placement; // where they signed up
      if (offer) props.signup_offer = offer; // which incentive was promised
      if (chosenKey || placement || offer) attrs.properties = props;
      var body = {
        data: {
          type: 'subscription',
          attributes: {
            // distinct per popup so the consent "Source" tells the routes apart in Klaviyo
            custom_source: source || 'Popup',
            profile: { data: { type: 'profile', attributes: attrs } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      };
      return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
      });
    }

    // Fire-and-forget analytics event so each route gets its own metric + can trigger its own
    // flow (filter the "Newsletter Signup" metric on `source`). Independent of the subscribe call.
    function trackEvent(email) {
      var body = {
        data: {
          type: 'event',
          attributes: {
            properties: {
              placement: placement || 'unknown',
              offer: offer || 'none',
              signup_goal: chosenKey || null,
            },
            metric: { data: { type: 'metric', attributes: { name: 'Newsletter Signup' } } },
            profile: { data: { type: 'profile', attributes: { email: email } } },
          },
        },
      };
      return fetch(KLAVIYO_EVENTS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
      });
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var email = (emailInput.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          showStatus(errorText);
          emailInput.focus();
          return;
        }
        if (!companyId || !listId) {
          showStatus(errorText);
          return;
        }
        if (status) status.hidden = true;
        setBusy(true);
        subscribe(email)
          .then(function (res) {
            if (res.status === 202) {
              writeState({ signedUp: true });
              trackEvent(email).catch(function () {}); // analytics only — never block the flow
              showScreen(3);
            } else {
              setBusy(false);
              showStatus(errorText);
            }
          })
          .catch(function () {
            setBusy(false);
            showStatus(errorText);
          });
      });
    }

    /* ---- register this popup so explicit openers can target it by handle ---- */
    var popupHandle = root.getAttribute('data-sg-popup-handle') || '';
    if (popupHandle) registry[popupHandle] = open;
    if (!firstOpen) firstOpen = open;

    /* ---- automatic trigger: viewed, then scrolled out of view (either direction).
       Only where the trigger element exists and the frequency cap allows it. ---- */
    var triggerEl = triggerSelector ? document.querySelector(triggerSelector) : null;
    if (triggerEl && !suppressed(frequencyDays) && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var e = entries[i];
            if (e.isIntersecting) {
              hasViewed = true;
            } else if (hasViewed && !opened) {
              open();
              io.disconnect();
            }
          }
        },
        { threshold: 0 },
      );
      io.observe(triggerEl);
    }
  }

  // Explicit openers: any [data-sg-popup-open] element opens a popup on click, bypassing the
  // frequency cap (the visitor asked for it). The attribute value names the target popup's
  // handle; empty/unknown falls back to the first popup on the page. Wired once, after every
  // popup has registered, so an opener can target any instance.
  function wireOpeners() {
    var openers = document.querySelectorAll('[data-sg-popup-open]');
    for (var o = 0; o < openers.length; o++) {
      (function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          var id = el.getAttribute('data-sg-popup-open');
          var fn = id && registry[id] ? registry[id] : firstOpen;
          if (fn) fn();
        });
      })(openers[o]);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-sg-popup]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
    wireOpeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
